/**
 * Authentication route utilities
 * Shared logic for determining which routes require authentication
 */

/**
 * Centralized route path constants.
 * Use these instead of hardcoding route strings throughout the codebase.
 */
export const ROUTES = {
  TENANT_SELECTION: '/tenant-selection',
  DASHBOARD: '/dashboard',
  AGENTS: '/agents',
  MCP_TESTER: '/mcp-tester',
  DOCS: '/docs',
  PROFILE: '/profile',
  ACCOUNT_SETTINGS: '/account-settings',
  AUTH_ERROR: '/auth/error',
  ONBOARDING: '/onboarding',
  TENANT_ONBOARDING: '/tenant-onboarding',
  ENROLLMENT_INVITATION: '/enrollment-invitation',
} as const;

/**
 * Router IDs for useRouteLoaderData and other router utilities.
 */
export const ROUTE_IDS = {
  TENANT_ROOT: 'tenant-root',
} as const;

// Routes that don't require authentication
const PUBLIC_PATHS = [ROUTES.TENANT_SELECTION, ROUTES.AUTH_ERROR] as const;

// Route prefixes that don't require authentication
const PUBLIC_PATH_PREFIXES = [
  ROUTES.ONBOARDING,
  ROUTES.ENROLLMENT_INVITATION,
] as const;

/**
 * Check if a given path is public (doesn't require authentication)
 * @param pathname - The URL pathname to check
 * @returns true if the path is public, false otherwise
 */
export function isPublicPath(pathname: string): boolean {
  // Check exact matches
  if (PUBLIC_PATHS.includes(pathname as (typeof PUBLIC_PATHS)[number])) {
    return true;
  }

  // Check prefix matches (e.g., /onboarding/:token)
  if (PUBLIC_PATH_PREFIXES.some(prefix => pathname.startsWith(prefix))) {
    return true;
  }

  // Matched by auth-loader/auth-utils to skip auth on tenant-prefixed public pages.
  const tenantPrefixedPattern =
    /^\/[^/]+\/(auth\/error|onboarding\/.+|tenant-onboarding\/.+|enrollment-invitation\/.+|pair\/.+)$/;

  if (tenantPrefixedPattern.test(pathname)) {
    return true;
  }

  return false;
}

/**
 * Check if authentication should be verified for a given path
 * @param pathname - The URL pathname to check
 * @returns true if auth should be checked, false for public paths
 */
export function shouldCheckAuth(pathname: string): boolean {
  return !isPublicPath(pathname);
}

/**
 * Get the list of public paths (for reference)
 */
export function getPublicPaths(): readonly string[] {
  return PUBLIC_PATHS;
}
